import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { CalendarIcon, ClockIcon, CheckCircle2Icon, ArrowRightIcon, ArrowLeftIcon, Loader2Icon, ChevronLeftIcon, ChevronRightIcon, UserIcon, MailIcon, PhoneIcon, BuildingIcon } from 'lucide-react';
const MAIL_ENDPOINT = 'https://scan.noorddigital.nl/mail.php';

// Rocco beschikbaar: wo(3) do(4) vr(5) za(6), 09:00–17:00, 45 min slots
const AVAILABLE_DAYS = [3, 4, 5, 6];
const TIME_SLOTS = ['09:00', '09:45', '10:30', '11:15', '12:00', '12:45', '13:30', '14:15', '15:00', '15:45', '16:15'];
const MONTHS_NL = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
const WEEKDAYS_NL = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const WEEKDAYS_FULL = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
async function sendViaPHPMailer(params: Record<string, string>) {
  const body = new URLSearchParams(params);
  const res = await fetch(MAIL_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: body.toString()
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}
function daysInMonth(y: number, m: number) {
  return new Date(y, m + 1, 0).getDate();
}
function toMondayIndex(d: number) {
  return (d + 6) % 7;
}
function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function isPast(d: Date) {
  return startOfDay(d) < startOfDay(new Date());
}
function isToday(d: Date) {
  return startOfDay(d).getTime() === startOfDay(new Date()).getTime();
}
function sameDay(a: Date | null, b: Date | null) {
  return !!a && !!b && startOfDay(a).getTime() === startOfDay(b).getTime();
}
function formatLong(d: Date) {
  return `${WEEKDAYS_FULL[d.getDay()]} ${d.getDate()} ${MONTHS_NL[d.getMonth()].toLowerCase()} ${d.getFullYear()}`;
}
function formatShort(d: Date) {
  return `${d.getDate()} ${MONTHS_NL[d.getMonth()].toLowerCase()}`;
}
function SonarPulse() {
  return <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] pointer-events-none z-0 opacity-30" data-id="element-16">
      {[0, 1, 2].map(i => <motion.div key={i} className="absolute inset-0 rounded-full border-[2px] border-brand-arctic/40" initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: [0, 1],
      opacity: [0.8, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      delay: i * 1.33,
      ease: 'easeOut'
    }} data-id="element-17" />)}
    </div>;
}
type Step = 'pick' | 'details' | 'success';
export function Booking() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('pick');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [sessionSeed] = useState(() => Math.floor(Math.random() * 1e9));
  const getBookedSlots = useMemo(() => {
    const cache = new Map<string, Set<string>>();
    return (date: Date): Set<string> => {
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      const cached = cache.get(key);
      if (cached) return cached;
      let h = 2166136261 ^ sessionSeed;
      const seedStr = key;
      for (let i = 0; i < seedStr.length; i++) {
        h ^= seedStr.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      let state = h >>> 0;
      const rand = () => {
        state = state + 0x6d2b79f5 | 0;
        let t = Math.imul(state ^ state >>> 15, 1 | state);
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
      };
      const count = 2 + Math.floor(rand() * 3);
      const pool = [...TIME_SLOTS];
      const taken = new Set<string>();
      for (let i = 0; i < count && pool.length > 0; i++) {
        const idx = Math.floor(rand() * pool.length);
        taken.add(pool.splice(idx, 1)[0]);
      }
      cache.set(key, taken);
      return taken;
    };
  }, [sessionSeed]);
  const bookedForSelected = useMemo(() => selectedDate ? getBookedSlots(selectedDate) : new Set<string>(), [selectedDate, getBookedSlots]);
  const calendarRows = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const total = daysInMonth(viewYear, viewMonth);
    const startCol = toMondayIndex(first.getDay());
    const cells: ({
      day: number;
      date: Date;
    } | null)[] = [];
    for (let i = 0; i < startCol; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push({
      day: d,
      date: new Date(viewYear, viewMonth, d)
    });
    while (cells.length % 7 !== 0) cells.push(null);
    const rows: (typeof cells)[] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [viewYear, viewMonth]);
  const canGoPrev = !(viewYear === today.getFullYear() && viewMonth === today.getMonth());
  function prevMonth() {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else setViewMonth(viewMonth - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else setViewMonth(viewMonth + 1);
  }
  function isAvailable(d: Date) {
    return !isPast(d) && AVAILABLE_DAYS.includes(d.getDay());
  }
  function selectDate(d: Date) {
    if (!isAvailable(d)) return;
    setSelectedDate(d);
    setSelectedTime(null);
  }
  function handleFormChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  }
  function validate() {
    if (!form.name.trim()) return 'Vul je naam in.';
    if (!form.email.trim()) return 'Vul je e-mailadres in.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Vul een geldig e-mailadres in.';
    if (!form.phone.trim()) return 'Vul je telefoonnummer in.';
    return null;
  }
  async function handleSubmit() {
    const err = validate();
    if (err) {
      setErrorMsg(err);
      return;
    }
    if (!selectedDate || !selectedTime) return;
    setErrorMsg(null);
    setSubmitting(true);
    const dateLong = formatLong(selectedDate);
    try {
      // Mail 1: interne notificatie naar Noord Digital
      await sendViaPHPMailer({
        naam: form.name.trim(),
        email: form.email.trim(),
        telefoon: form.phone.trim(),
        bericht: `Nieuwe CorePrint-afspraak\n\nBedrijf: ${form.company.trim() || 'Niet opgegeven'}\nDatum: ${dateLong}\nTijd: ${selectedTime} (45 min)`,
        ontvanger: 'info@noorddigital.nl',
        onderwerp: `Nieuwe afspraak CorePrint — ${form.name.trim()}`
      });
      // Mail 2: bevestiging naar klant
      await sendViaPHPMailer({
        naam: 'Noord Digital',
        email: 'info@noorddigital.nl',
        telefoon: '+31 6 53 68 96 86',
        bericht: `Hoi ${form.name.trim()},\n\nJe afspraak staat bevestigd!\n\nDatum: ${dateLong}\nTijd: ${selectedTime} (45 minuten)\nOnline via Google Meet\n\nVragen? Mail info@noorddigital.nl\n\nMet vriendelijke groet,\nHet Noord Digital team`,
        ontvanger: form.email.trim(),
        onderwerp: `Bevestiging: je CorePrint-presentatie op ${dateLong}`
      });
      setStep('success');
    } catch (e: any) {
      setErrorMsg('Versturen mislukt. Probeer opnieuw of mail info@noorddigital.nl.');
    } finally {
      setSubmitting(false);
    }
  }
  function resetAll() {
    setStep('pick');
    setSelectedDate(null);
    setSelectedTime(null);
    setForm({
      name: '',
      email: '',
      phone: '',
      company: ''
    });
    setErrorMsg(null);
  }
  return <section id="gesprek" ref={ref} className="py-24 relative overflow-hidden noise-overlay gradient-mesh" style={{
    background: 'linear-gradient(160deg, #050A14 0%, #0D1628 100%)'
  }} data-id="element-18">
      <div className="absolute inset-0 grid-bg opacity-50" data-id="element-19" />
      <motion.div className="absolute top-0 right-0 w-[700px] h-[700px] opacity-15 pointer-events-none blur-3xl" style={{
      background: 'radial-gradient(ellipse, #0066FF, transparent)'
    }} animate={{
      scale: [1, 1.2, 1],
      opacity: [0.1, 0.2, 0.1]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-20" />

      <div className="relative z-10 max-w-[1100px] mx-auto px-6" data-id="element-21">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-20 items-center" data-id="element-22">
          {/* LEFT */}
          <motion.div className="relative" initial={{
          opacity: 0,
          x: -40
        }} animate={inView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.8,
          type: 'spring',
          stiffness: 80
        }} data-id="element-23">
            <SonarPulse data-id="element-24" />
            <div className="relative z-10" data-id="element-25">
              <div className="section-label-dark" data-id="element-26">Gratis presentatie</div>
              <h2 className="text-[clamp(2.2rem,4vw,3.2rem)] font-black text-white leading-tight tracking-tight mb-7" data-id="element-27">
                Ontdek waar<br data-id="element-28" />
                <span className="gradient-text" data-id="element-29">jouw vis zit.</span>
              </h2>
              <p className="text-white/55 text-[1.08rem] leading-relaxed mb-10" data-id="element-30">
                In 45 minuten laten we je zien hoe de sonar-aanpak werkt, of jouw markt beschikbaar is, en wat het jou concreet kan opleveren. Volledig vrijblijvend.
              </p>
              <div className="space-y-5 mb-12" data-id="element-31">
                {[{
                icon: ClockIcon,
                text: '45 minuten, online via Google Meet'
              }, {
                icon: CheckCircle2Icon,
                text: 'Vrijblijvend, geen verplichtingen'
              }, {
                icon: CheckCircle2Icon,
                text: 'Direct antwoord of jouw markt vrij is'
              }, {
                icon: CalendarIcon,
                text: 'Beschikbaar woensdag tot zaterdag, van 09:00 tot 17:00'
              }].map((item, i) => <motion.div key={item.text} className="flex items-center gap-4" initial={{
                opacity: 0,
                x: -20
              }} animate={inView ? {
                opacity: 1,
                x: 0
              } : {}} transition={{
                delay: 0.3 + i * 0.1,
                type: 'spring'
              }} data-id="element-32">
                    <item.icon size={18} className="text-brand-arctic flex-shrink-0" data-id="element-33" />
                    <span className="text-white/70 text-[0.98rem] font-medium" data-id="element-34">{item.text}</span>
                  </motion.div>)}
              </div>
              <motion.div className="flex items-center gap-4 p-5 rounded-2xl bg-black/20 backdrop-blur-md" style={{
              border: '1px solid rgba(0,102,255,0.3)'
            }} animate={{
              boxShadow: ['0 0 0 0 rgba(0,102,255,0.2)', '0 0 0 8px rgba(0,102,255,0)']
            }} transition={{
              duration: 2,
              repeat: Infinity
            }} data-id="element-35">
                <motion.span className="w-3 h-3 rounded-full bg-[#28C840] flex-shrink-0" style={{
                boxShadow: '0 0 10px #28C840'
              }} animate={{
                scale: [1, 1.2, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-36" />
                <span className="text-white/75 text-sm font-medium" data-id="element-37">
                  <strong className="text-white font-bold" data-id="element-38">Let op:</strong>{' '}
                  Zodra een concurrent in jouw markt boekt, is de exclusiviteit weg.
                </span>
              </motion.div>
            </div>
          </motion.div>

          {/* RIGHT — widget */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.3,
          type: 'spring',
          stiffness: 80
        }} data-id="element-39">
            <div className="rounded-[2rem] overflow-hidden" style={{
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
            background: 'rgba(13,18,32,0.98)'
          }} data-id="element-40">
              {/* Header */}
              <div className="px-8 py-5 flex items-center justify-between" style={{
              background: 'rgba(255,255,255,0.04)',
              borderBottom: '1px solid rgba(255,255,255,0.06)'
            }} data-id="element-41">
                <div className="flex items-center gap-3" data-id="element-42">
                  {step === 'details' && <button type="button" onClick={() => {
                  setStep('pick');
                  setErrorMsg(null);
                }} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all" data-id="element-43">
                      <ArrowLeftIcon size={16} data-id="element-44" />
                    </button>}
                  <CalendarIcon size={20} className="text-brand-arctic" data-id="element-45" />
                  <span className="text-white font-bold text-base" data-id="element-46">
                    {step === 'pick' && 'Kies een moment'}
                    {step === 'details' && 'Jouw gegevens'}
                    {step === 'success' && 'Afspraak bevestigd'}
                  </span>
                </div>
                {step !== 'success' && <div className="flex items-center gap-1.5" data-id="element-47">
                    <div className={`h-1.5 rounded-full transition-all ${step === 'pick' ? 'w-8 bg-brand-arctic' : 'w-3 bg-brand-arctic/40'}`} data-id="element-48" />
                    <div className={`h-1.5 rounded-full transition-all ${step === 'details' ? 'w-8 bg-brand-arctic' : 'w-3 bg-white/15'}`} data-id="element-49" />
                  </div>}
              </div>

              <AnimatePresence mode="wait" data-id="element-50">
                {/* STAP 1 */}
                {step === 'pick' && <motion.div key="pick" initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} transition={{
                duration: 0.22
              }} className="p-7" data-id="element-51">
                    <div className="flex items-center justify-between mb-5" data-id="element-52">
                      <button type="button" onClick={prevMonth} disabled={!canGoPrev} className="w-9 h-9 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-all" data-id="element-53">
                        <ChevronLeftIcon size={18} data-id="element-54" />
                      </button>
                      <span className="text-white font-bold" data-id="element-55">{MONTHS_NL[viewMonth]} {viewYear}</span>
                      <button type="button" onClick={nextMonth} className="w-9 h-9 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all" data-id="element-56">
                        <ChevronRightIcon size={18} data-id="element-57" />
                      </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1.5 mb-2" data-id="element-58">
                      {WEEKDAYS_NL.map(d => <div key={d} className="text-center text-white/30 text-xs font-bold py-1" data-id="element-59">{d}</div>)}
                    </div>

                    {calendarRows.map((row, ri) => <div key={ri} className="grid grid-cols-7 gap-1.5 mb-1.5" data-id="element-60">
                        {row.map((cell, ci) => {
                    if (!cell) return <div key={ci} className="h-10" data-id="element-61" />;
                    const avail = isAvailable(cell.date);
                    const selected = sameDay(selectedDate, cell.date);
                    const todayDot = isToday(cell.date);
                    return <motion.button type="button" key={ci} onClick={() => selectDate(cell.date)} disabled={!avail} className={`h-10 rounded-lg text-sm font-bold transition-all duration-200 relative ${selected ? 'bg-brand-primary text-white shadow-glow-sm' : avail ? 'text-white/80 hover:bg-white/10 hover:text-white bg-white/[0.03]' : 'text-white/15 cursor-not-allowed'}`} whileHover={avail && !selected ? {
                      scale: 1.06
                    } : {}} whileTap={avail ? {
                      scale: 0.94
                    } : {}} data-id="element-62">
                              {cell.day}
                              {todayDot && !selected && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-brand-arctic" data-id="element-63" />}
                            </motion.button>;
                  })}
                      </div>)}

                    <div className="mt-7 mb-6" data-id="element-64">
                      <div className="text-white/50 text-xs mb-3 font-semibold uppercase tracking-wider" data-id="element-65">
                        {selectedDate ? <> Tijden — <span className="text-white/80" data-id="element-66">{formatShort(selectedDate)}</span></> : 'Kies eerst een datum'}
                      </div>
                      <div className="grid grid-cols-3 gap-2" data-id="element-67">
                        {TIME_SLOTS.map(t => {
                      const dateChosen = !!selectedDate;
                      const taken = bookedForSelected.has(t);
                      const enabled = dateChosen && !taken;
                      const active = selectedTime === t;
                      return <motion.button type="button" key={t} disabled={!enabled} onClick={() => enabled && setSelectedTime(t)} className={`relative py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${active ? 'bg-brand-primary text-white' : taken && dateChosen ? 'text-white/25 cursor-not-allowed line-through' : enabled ? 'text-white/75 hover:bg-white/10 hover:text-white' : 'text-white/20 cursor-not-allowed'}`} style={{
                        background: active ? undefined : taken && dateChosen ? 'rgba(255,255,255,0.015)' : 'rgba(255,255,255,0.04)',
                        border: active ? '1px solid #0066FF' : taken && dateChosen ? '1px dashed rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.08)'
                      }} whileHover={enabled && !active ? {
                        scale: 1.04
                      } : {}} whileTap={enabled ? {
                        scale: 0.96
                      } : {}} data-id="element-68">
                              {t}
                              {taken && dateChosen && <span className="absolute -top-1.5 -right-1.5 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-white/10 text-white/40 border border-white/10" data-id="element-69">Bezet</span>}
                            </motion.button>;
                    })}
                      </div>
                    </div>

                    <div className="p-3 rounded-xl mb-3 text-xs" style={{
                  background: selectedDate && selectedTime ? 'rgba(0,102,255,0.08)' : 'rgba(255,255,255,0.02)',
                  border: selectedDate && selectedTime ? '1px solid rgba(0,102,255,0.25)' : '1px solid rgba(255,255,255,0.05)'
                }} data-id="element-70">
                      {selectedDate && selectedTime ? <>
                          <div className="text-white/50 mb-0.5" data-id="element-71">Geselecteerd</div>
                          <div className="text-white font-bold" data-id="element-72">{formatShort(selectedDate)} · {selectedTime}</div>
                        </> : <span className="text-white/40" data-id="element-73">{!selectedDate ? 'Kies een datum en tijd' : 'Kies nu een tijd'}</span>}
                    </div>

                    <motion.button type="button" onClick={() => {
                  if (selectedDate && selectedTime) {
                    setStep('details');
                    setErrorMsg(null);
                  }
                }} disabled={!selectedDate || !selectedTime} className="w-full py-4 rounded-2xl font-black text-white flex items-center justify-center gap-3 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed" style={{
                  background: selectedDate && selectedTime ? 'linear-gradient(135deg,#0052CC,#0066FF)' : 'rgba(255,255,255,0.05)'
                }} whileHover={selectedDate && selectedTime ? {
                  scale: 1.02,
                  boxShadow: '0 8px 32px rgba(0,102,255,0.4)'
                } : {}} whileTap={selectedDate && selectedTime ? {
                  scale: 0.98
                } : {}} data-id="element-74">
                      Volgende: gegevens <ArrowRightIcon size={18} data-id="element-75" />
                    </motion.button>
                    <p className="text-center text-white/30 text-xs mt-4" data-id="element-76">Gratis · Vrijblijvend · 45 minuten</p>
                  </motion.div>}

                {/* STAP 2 */}
                {step === 'details' && <motion.div key="details" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: 20
              }} transition={{
                duration: 0.22
              }} className="p-7" data-id="element-77">
                    <div className="flex items-start gap-3 p-4 rounded-xl mb-6" style={{
                  background: 'rgba(0,102,255,0.08)',
                  border: '1px solid rgba(0,102,255,0.25)'
                }} data-id="element-78">
                      <CalendarIcon size={18} className="text-brand-arctic flex-shrink-0 mt-0.5" data-id="element-79" />
                      <div className="flex-1" data-id="element-80">
                        <div className="text-white font-bold text-sm" data-id="element-81">{selectedDate && formatLong(selectedDate)}</div>
                        <div className="text-white/60 text-xs mt-0.5" data-id="element-82">{selectedTime} · 45 minuten · Online via Google Meet</div>
                      </div>
                      <button type="button" onClick={() => setStep('pick')} className="text-brand-arctic text-xs font-bold hover:underline flex-shrink-0" data-id="element-83">Wijzigen</button>
                    </div>

                    <div className="space-y-3" data-id="element-84">
                      <Field label="Naam" name="name" value={form.name} onChange={handleFormChange} placeholder="Voor- en achternaam" icon={UserIcon} required autoFocus data-id="element-85" />
                      <Field label="E-mailadres" name="email" value={form.email} onChange={handleFormChange} placeholder="jij@bedrijf.nl" icon={MailIcon} required type="email" data-id="element-86" />
                      <Field label="Telefoonnummer" name="phone" value={form.phone} onChange={handleFormChange} placeholder="06 12 34 56 78" icon={PhoneIcon} required type="tel" data-id="element-87" />
                      <Field label="Bedrijf (optioneel)" name="company" value={form.company} onChange={handleFormChange} placeholder="Bedrijfsnaam" icon={BuildingIcon} data-id="element-88" />
                    </div>

                    {errorMsg && <div className="mt-4 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-medium" data-id="element-89">{errorMsg}</div>}

                    <motion.button type="button" onClick={handleSubmit} disabled={submitting} className="w-full mt-5 py-4 rounded-2xl font-black text-white flex items-center justify-center gap-3 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed" style={{
                  background: 'linear-gradient(135deg,#0052CC,#0066FF)'
                }} whileHover={!submitting ? {
                  scale: 1.02,
                  boxShadow: '0 8px 32px rgba(0,102,255,0.4)'
                } : {}} whileTap={!submitting ? {
                  scale: 0.98
                } : {}} data-id="element-90">
                      {submitting ? <><Loader2Icon size={18} className="animate-spin" data-id="element-91" /> Versturen…</> : <><CheckCircle2Icon size={20} data-id="element-92" /> Bevestig afspraak</>}
                    </motion.button>

                    <p className="text-center text-white/30 text-[11px] mt-3" data-id="element-93">
                      Je gaat akkoord met onze{' '}
                      <a href="https://noorddigital.nl/privacy" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white underline" data-id="element-94">privacyverklaring</a>.
                    </p>
                  </motion.div>}

                {/* STAP 3 */}
                {step === 'success' && <motion.div key="success" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.3
              }} className="p-10 text-center" data-id="element-95">
                    <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 200,
                  delay: 0.1
                }} className="w-20 h-20 rounded-full bg-brand-primary/20 flex items-center justify-center mx-auto mb-6" data-id="element-96">
                      <CheckCircle2Icon size={40} className="text-brand-arctic" data-id="element-97" />
                    </motion.div>
                    <h3 className="text-white font-black text-2xl mb-3" data-id="element-98">Je afspraak staat</h3>
                    <p className="text-white/60 text-sm leading-relaxed mb-2 max-w-xs mx-auto" data-id="element-99">
                      We zien je <span className="text-white font-semibold" data-id="element-100">{selectedDate && formatLong(selectedDate)}</span> om <span className="text-white font-semibold" data-id="element-101">{selectedTime}</span>.
                    </p>
                    <p className="text-white/50 text-sm mb-8" data-id="element-102">
                      Bevestiging verstuurd naar <span className="text-white/80 font-semibold" data-id="element-103">{form.email}</span>.
                    </p>
                    <button type="button" onClick={resetAll} className="text-brand-arctic text-sm font-bold hover:underline" data-id="element-104">Nieuwe afspraak inplannen</button>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
type ComponentType<P = {}> = React.ComponentType<P>;
function Field({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  icon: Icon,
  autoFocus
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  icon?: ComponentType<{
    size?: number;
    className?: string;
  }>;
  autoFocus?: boolean;
}) {
  return <label className="block" data-id="element-105">
      <span className="block text-white/60 text-xs font-semibold mb-1.5" data-id="element-106">
        {label}{required && <span className="text-brand-arctic ml-1" data-id="element-107">*</span>}
      </span>
      <div className="relative" data-id="element-108">
        {Icon && <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" data-id="element-109" />}
        <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} autoFocus={autoFocus} className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-brand-arctic/60 focus:bg-white/[0.07] transition-all`} data-id="element-110" />
      </div>
    </label>;
}